import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../../shared/utils/logger';
import { getAccountBudget, updateMonthlyBudget } from '../../../shared/utils/comptabiliteService';
import { formatComptabiliteMoney } from '../utils/comptabiliteSaudiFormat';

/** 12-month budget grid — used from Plan comptable. */
export default function MonthlyBudgetEditor({ isOpen, onClose, accountNumber, accountName, fiscalYearId }) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.analysesRapportsPage.${key}`, opts);

  const [budgetData, setBudgetData] = useState({
    month_01: 0, month_02: 0, month_03: 0, month_04: 0,
    month_05: 0, month_06: 0, month_07: 0, month_08: 0,
    month_09: 0, month_10: 0, month_11: 0, month_12: 0,
  });

  useEffect(() => {
    if (!isOpen || !accountNumber || !fiscalYearId) return;

    const loadBudget = async () => {
      try {
        const budget = await getAccountBudget(accountNumber, fiscalYearId);
        if (budget) {
          setBudgetData({
            month_01: budget.month_01 || 0,
            month_02: budget.month_02 || 0,
            month_03: budget.month_03 || 0,
            month_04: budget.month_04 || 0,
            month_05: budget.month_05 || 0,
            month_06: budget.month_06 || 0,
            month_07: budget.month_07 || 0,
            month_08: budget.month_08 || 0,
            month_09: budget.month_09 || 0,
            month_10: budget.month_10 || 0,
            month_11: budget.month_11 || 0,
            month_12: budget.month_12 || 0,
          });
        }
      } catch (error) {
        logger.error('Load budget error:', error);
      }
    };

    loadBudget();
  }, [isOpen, accountNumber, fiscalYearId]);

  const monthNames = useMemo(
    () => [
      p('month_january'), p('month_february'), p('month_march'), p('month_april'),
      p('month_may'), p('month_june'), p('month_july'), p('month_august'),
      p('month_september'), p('month_october'), p('month_november'), p('month_december'),
    ],
    [t]
  );

  const handleMonthChange = (monthIndex, value) => {
    const monthKey = `month_${String(monthIndex + 1).padStart(2, '0')}`;
    setBudgetData({ ...budgetData, [monthKey]: parseFloat(value) || 0 });
  };

  const calculateTotal = () =>
    Object.values(budgetData).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  const handleSave = async () => {
    if (!accountNumber || !fiscalYearId) {
      alert(p('alert_missing_account'));
      return;
    }

    try {
      await updateMonthlyBudget({
        fiscal_year_id: fiscalYearId,
        account_number: accountNumber,
        ...budgetData,
      });
      alert(p('alert_budget_saved'));
      onClose();
    } catch (error) {
      logger.error('Save budget error:', error);
      alert(p('error_prefix', { message: error.message }));
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={onClose} role="presentation">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-slate-800 via-blue-900/50 to-slate-800 border-2 border-blue-400/50 rounded-2xl p-6 max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">{p('budget_monthly_title')}</h2>
            <p className="text-white/60 text-sm mt-1">{accountNumber} - {accountName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {monthNames.map((month, index) => (
              <div key={month}>
                <label className="block w-full text-white/70 text-xs mb-1.5 font-medium text-right">{month}</label>
                <input
                  type="number"
                  value={budgetData[`month_${String(index + 1).padStart(2, '0')}`]}
                  onChange={(e) => handleMonthChange(index, e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                  step="0.01"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-400/30 rounded-xl">
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold">{p('budget_annual_total')}</span>
              <span className="text-2xl font-bold text-blue-300">{formatComptabiliteMoney(calculateTotal())}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all"
          >
            {p('cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-xl font-medium transition-all flex items-center space-x-2"
          >
            <Save size={16} />
            <span>{p('save')}</span>
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
